import { Base64 } from 'js-base64';
import { Common, color } from '@stockbit/ui';
import { NewTag } from 'global/Header/ProfileMenu/styled';
import { affiliateActions } from 'features/community/analytics';
import { PAGE_SOURCE_INDEX } from 'features/user/analytics/ProfileAnalytics';
import { isClient } from 'constants/app';
import { handleClickSupport } from './crisp';

const { Flex } = Common;

// helpers
const encodeBase64FromJS = (object) => {
  let result = object;
  if (typeof object === 'object') {
    result = JSON.stringify(object);
  }

  return Base64.encode(result);
};

const decodeBase64ToJS = (base64) => {
  const decoded = Base64.decode(base64);
  let result = decoded;
  try {
    result = JSON.parse(decoded);
  } catch (e) {
    result = decoded;
  }
  return result;
};

// local storage utils
const saveObject = (key, object) => {
  window.localStorage.setItem('location', window.location.origin);
  window.localStorage.setItem(key, encodeBase64FromJS(object));
};

// Save user credentials to local storage
const setLocalUser = (accessUser) => saveObject('au', accessUser);
const setLocalAccess = (accessToken) => saveObject('at', accessToken);
const setLocalAccessExp = (accessTokenExp) => saveObject('ate', accessTokenExp);
const setLocalRefresh = (refreshToken) => saveObject('ar', refreshToken);
const setLocalRefreshExp = (refreshTokenExp) =>
  saveObject('are', refreshTokenExp);
const setLocalChatToken = (wsToken) => saveObject('wst', wsToken);
const setLocalIntercomSettings = (intercom) => saveObject('icom', intercom);
const setLocalCrispSettings = (crisp) => saveObject('crisp', crisp);
const setLocalTempUserKey = (tempUserKey) => saveObject('tmpkey', tempUserKey);

const pageSourceIndex = PAGE_SOURCE_INDEX.findIndex(
  (sourceIndex) => sourceIndex === 'My Profile',
);

const getOS = () => {
  if (!isClient()) {
    return 'others';
  }

  const { userAgent } = navigator;

  if (/Windows/.test(userAgent)) {
    return 'Windows';
  }
  if (/Mac OS|Macintosh/.test(userAgent)) {
    return 'macOS';
  }
  return 'others';
};

// Build user profile menu
const profileMenu = (data) => {
  if (!data) {
    return [];
  }

  const proText = data.ispro ? 'Extend PRO Subscription' : 'Upgrade to PRO';
  // Countries that will not implement pro features
  const noProCountries = ['MY', 'SG'];
  // Countries that will implement some features
  const implementCountries = ['ID'];

  const isTipJarMenuItemAvailable = implementCountries.some(
    (country) => data.country === country,
  );
  const isProAvailable = !noProCountries.some(
    (country) => data.country === country,
  );

  const tipJarMenuItem = isTipJarMenuItemAvailable
    ? [{ label: 'My Tip Jar', logo: 'icon-setting_tipjar', url: '/tipjar' }]
    : [];

  const communityItem =
    data.isCommunityAccessGranted && data.country === 'ID'
      ? [
          {
            label: (
              <Flex
                align="center"
                style={{ gap: 5 }}
                onClick={() => {
                  affiliateActions.externalCommunitySelected();
                }}
              >
                External Community <NewTag>New</NewTag>
              </Flex>
            ),
            url: '/community',
            logo: 'icon-community',
          },
        ]
      : [];

  const menuItemGroup1 = [
    {
      label: 'My Profile',
      logo: 'icon-menu_profile',
      url: `/${data.username}?source=${pageSourceIndex}`,
    },
    { label: 'Settings', logo: 'icon-menu_setting', url: '/profile/edit' },
    ...communityItem,
    ...tipJarMenuItem,
  ];

  const proItems = [
    { label: proText, logo: 'icon-setting_subscribe', url: '/shop' },
  ];

  const menuItemGroup2 = isProAvailable ? proItems : undefined;

  const menuItemGroup3 = [
    { label: 'Rules', logo: 'icon-setting_rules', url: '/rules' },
    {
      label: 'Help',
      logo: 'icon-setting_help',
      url: 'https://help.stockbit.com',
    },
    {
      label: 'Live Support',
      logo: 'icon-setting_support',
      url: '',
      onClick: (e) => {
        e.preventDefault();
        handleClickSupport();
      },
    },
    { label: 'Logout', logo: 'icon-setting_logout', url: '/logout' },
  ];

  const menuDownload = [
    {
      type: 'download',
      label: 'Download Desktop App',
      image: '/images/download-icon.svg',
      textColor: color.primary,
      url: '',
      onClick: (e) => {
        e.preventDefault();
      },
    },
  ];

  return [menuItemGroup1, menuItemGroup2, menuItemGroup3, menuDownload].filter(
    (menuItemGroup) => menuItemGroup !== undefined,
  );
};

export default {
  setLocalUser,
  setLocalAccess,
  setLocalAccessExp,
  setLocalRefresh,
  setLocalRefreshExp,
  setLocalChatToken,
  setLocalIntercomSettings,
  setLocalCrispSettings,
  setLocalTempUserKey,
  encodeBase64FromJS,
  decodeBase64ToJS,
  profileMenu,
  getOS,
};
